<script setup lang="ts">
    import { computed, toRef } from 'vue'
    import type { AttributeDependenciesDialogViewModel } from '@Alternatives/Attributes/Components/AttributeDependenciesDialog'
    import type { DependentEntityInfo } from '@common/types'
    import { dynamicUiRoutes } from '@common/dynamicUiRoutes'
    import { useRoute } from 'vue-router'

    // ---------------- props / emits ----------------

    const props = defineProps<{
        viewModel: AttributeDependenciesDialogViewModel
    }>()

    // ---------------- router ----------------

    const route = useRoute()

    // ---------------- shortcuts ----------------

    const vm = toRef(props, 'viewModel')

    // ---------------- derived ----------------

    const opened = computed(() => vm.value?.opened ?? false)
    const rm = computed(() => vm.value.rm)

    // ---------------- url helpers ----------------

    function url_MeasurementsPage(e: DependentEntityInfo): string
    {
        return dynamicUiRoutes.Criteria_MeasurementsPage(
            route,
            e.WorkspaceId,
            e.TchProjectId,
            e.EvalModelId,
            e.ScoringEnabled
        )
    }

    function url_EditFormPage(e: DependentEntityInfo): string
    {
        return dynamicUiRoutes.Alternatives_EditFormPage(
            route,
            e.WorkspaceId,
            e.TchProjectId,
            Number.parseInt(e.Id)
        )
    }

    function url_ReportViewPage(e: DependentEntityInfo): string
    {
        return dynamicUiRoutes.Reports_ReportViewPage(
            route,
            e.WorkspaceId,
            e.TchProjectId,
            e.Id
        )
    }

    // ---------------- methods ----------------

    function close()
    {
        vm.value.close()
    }
</script>


<template>
    <div class="modal-mask" v-if="opened">
        <div class="modal-wrapper">
            <div class="modal-dialog modal-xl modal-dialog-scrollable">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title">Attribute dependencies: {{vm.attributeName}}</h5>
                        <button type="button" class="close" @click="close">&times;</button>
                    </div>

                    <div class="modal-body">
                        <h4 v-if="!rm"><i class="fa fa-spinner fa-spin"></i> Checking dependencies...</h4>

                        <div v-if="rm">
                            <div class="alert alert-warning text-dark" role="alert">
                                <p>The attribute '<strong>{{rm.Name}}</strong>' is used in <strong>{{rm.Count}}</strong> places.</p>
                            </div>

                            <div class="card mt-3" v-if="rm.Forms.length > 0">
                                <div class="card-header">
                                    Forms ({{rm.Forms.length}})
                                </div>
                                <ul class="list-group list-group-flush">
                                    <li class="list-group-item" v-for="f in rm.Forms" :key="f.Id">
                                        <a class="text-primary" v-sanitizedHref="url_EditFormPage(f)">{{f.Name}}</a>
                                    </li>
                                </ul>
                            </div>

                            <div class="card mt-3" v-if="rm.Reports.length > 0">
                                <div class="card-header">
                                    Reports ({{rm.Reports.length}})
                                </div>
                                <ul class="list-group list-group-flush">
                                    <li class="list-group-item" v-for="r in rm.Reports" :key="r.Id">
                                        <a class="text-primary" target="_blank" v-sanitizedHref="url_ReportViewPage(r)">{{r.Name}}</a>
                                    </li>
                                </ul>
                            </div>

                            <div class="card mt-3" v-if="rm.Criteria.length > 0">
                                <div class="card-header">
                                    Criteria ({{rm.Criteria.length}})
                                </div>
                                <ul class="list-group list-group-flush">
                                    <li class="list-group-item" v-for="c in rm.Criteria" :key="c.Id">
                                        <a class="text-primary" target="_blank" v-sanitizedHref="url_MeasurementsPage(c)">{{c.Name}}</a><br />
                                        <small class="text-muted">{{c.Path}}</small>
                                    </li>
                                </ul>
                            </div>

                            <div class="card mt-3" v-if="rm.Lists.length > 0">
                                <div class="card-header">
                                    Lists ({{rm.Lists.length}})
                                </div>
                                <ul class="list-group list-group-flush">
                                    <li class="list-group-item" v-for="l in rm.Lists" :key="l.Id">
                                        <span>{{l.Name}}</span>
                                    </li>
                                </ul>
                            </div>

                            <div class="card mt-3" v-if="rm.CalcAttributes.length > 0">
                                <div class="card-header">
                                    Calculated attributes ({{rm.CalcAttributes.length}})
                                </div>
                                <ul class="list-group list-group-flush">
                                    <li class="list-group-item" v-for="attr in rm.CalcAttributes" :key="attr.Id">
                                        <span>{{attr.Name}}</span>
                                    </li>
                                </ul>
                            </div>
                            <div class="card mt-3" v-if="rm.Notifications.length > 0">
                                <div class="card-header">
                                    Notifications ({{rm.Notifications.length}})
                                </div>
                                <ul class="list-group list-group-flush">
                                    <li class="list-group-item" v-for="n in rm.Notifications" :key="n.Id">
                                        <span>{{n.Name}}</span>
                                    </li>
                                </ul>
                            </div>
                            <div class="card mt-3" v-if="rm.ScoringSurveys.length > 0">
                                <div class="card-header">
                                    Scoring ({{rm.ScoringSurveys.length}})
                                </div>
                                <ul class="list-group list-group-flush">
                                    <li class="list-group-item" v-for="s in rm.ScoringSurveys" :key="s.Id">
                                        <span>{{s.Name}}</span>
                                    </li>
                                </ul>
                            </div>
                            <div class="card mt-3" v-if="rm.EstimationsSurveys.length > 0">
                                <div class="card-header">
                                    Assessments ({{rm.EstimationsSurveys.length}})
                                </div>
                                <ul class="list-group list-group-flush">
                                    <li class="list-group-item" v-for="s in rm.EstimationsSurveys" :key="s.Id">
                                        <span>{{s.Name}}</span>
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>
                    <div class="modal-footer">
                        <button @click="close" type="button" class="btn btn-outline-secondary">Close</button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>